import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, School, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUBJECTS = ["School Visit", "Book Reading", "Press & Media", "Bulk Order", "Just Saying Hello"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: SUBJECTS[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent!", {
        description: "Thanks for reaching out — Sugarfoot will get back to you soon.",
      });
      setForm({ name: "", email: "", subject: SUBJECTS[0], message: "" });
    } catch (err) {
      toast.error("Oops, something went wrong", {
        description: "Please check the fields and try again.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="halftone relative border-t-2 border-ink py-20 sm:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <Reveal>
          <span className="sticker rotate-1 rounded-lg bg-white">Say hello</span>
          <h2 className="mt-5 font-head text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
            Bring Lil Momma &amp; Man-Man <span className="text-maroon">to your school.</span>
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-ink/70 sm:text-lg">
            Book readings, school visits, press, or bulk orders for your classroom — drop a line and let's make it
            happen.
          </p>
          <div className="mt-8 space-y-4">
            <div className="flex items-center gap-3 font-head text-sm font-bold uppercase tracking-wide text-ink/70">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl border-2 border-ink bg-banana shadow-pop-sm">
                <School className="h-5 w-5" />
              </span>
              Readings &amp; school visits
            </div>
            <div className="flex items-center gap-3 font-head text-sm font-bold uppercase tracking-wide text-ink/70">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl border-2 border-ink bg-crimson text-cream shadow-pop-sm">
                <Mail className="h-5 w-5" />
              </span>
              Replies within a few days
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="rounded-2xl border-2 border-ink bg-white p-6 shadow-pop-lg sm:p-8"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block font-mono text-xs font-bold uppercase tracking-widest text-ink/60">Full name</span>
                <input
                  data-testid="contact-input-name"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  className="comic-input"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block font-mono text-xs font-bold uppercase tracking-widest text-ink/60">Email</span>
                <input
                  data-testid="contact-input-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className="comic-input"
                />
              </label>
            </div>
            <label className="mt-5 block">
              <span className="mb-1.5 block font-mono text-xs font-bold uppercase tracking-widest text-ink/60">Subject</span>
              <select data-testid="contact-input-subject" value={form.subject} onChange={set("subject")} className="comic-input">
                {SUBJECTS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <label className="mt-5 block">
              <span className="mb-1.5 block font-mono text-xs font-bold uppercase tracking-widest text-ink/60">Message</span>
              <textarea
                data-testid="contact-input-message"
                required
                minLength={5}
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us about your school, event, or question…"
                className="comic-input resize-none"
              />
            </label>
            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={sending}
              className="mt-7 flex w-full items-center justify-center gap-2.5 rounded-full border-2 border-ink bg-maroon px-6 py-3.5 font-head text-base font-extrabold uppercase tracking-wide text-cream shadow-pop transition-all duration-200 hover:-translate-y-0.5 hover:shadow-pop-lg active:translate-y-0 active:shadow-pop-sm disabled:opacity-60"
            >
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              {sending ? "Sending…" : "Send message"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
